import { useMemo, useCallback } from "react";
import { BROKER_ACTIVACION } from "../../../constants/permisos";
import {
  calcularComisionEdificiosTotal,
  calcularImporteNetoIntermediario,
  calcularPorcentajeComisionIntermediario,
  calculateComission,
  obtenerNombreIntermediario,
} from "../../../utils";

/**
 * Hook que calcula ingresos mensuales y comisión de edificios de contratos (sales_contratos).
 *
 * Explota el JSONB `meses` de cada contrato y agrupa por mes según `facturafecha`.
 * ER solo cuenta meses realmente facturados (con facturafecha), no proyectados.
 */
export function useContratosCalcs({
  contratosChile,
  contratosPeru,
  selectedYear,
  modoCombinado,
  dolarFallback,
  getDolarValue,
  getDolarValueChile,
  getDolarValuePeru,
  dolarMensualChile,
  dolarMensualPeru,
  pais,
  edificios,
  edificiosChile,
  edificiosPeru,
  snapMap,
  snapMapChile,
  snapMapPeru,
  clientes,
  clientesChile,
  clientesPeru,
  intermediarios,
  intermediariosChile,
  intermediariosPeru,
}) {
  // Helper: calcula ingresos mensuales explotando JSONB meses
  const calcIngresosContratos = useCallback((data, filterYear, getDolarFn) => {
    const valores = Array(12).fill(0);
    data
      .filter((c) => !c.eliminada && c.estatus === "100%")
      .forEach((c) => {
        (c.meses || []).forEach((mesObj) => {
          if (!mesObj.facturafecha) return; // ER: solo facturado real
          const fecha = new Date(mesObj.facturafecha);
          if (isNaN(fecha.getTime())) return;
          const monthIndex = fecha.getMonth();
          const year = fecha.getFullYear();
          if (year === filterYear && monthIndex >= 0 && monthIndex < 12) {
            const importe = Number(mesObj.importe) || 0;
            valores[monthIndex] += c.moneda_dolar ? importe * getDolarFn(mesObj.facturafecha) : importe;
          }
        });
      });
    return valores;
  }, []);

  // Helper: backward-compat para campos por tipo de pantalla
  const resolverCamposTipo = (mesObj, edificiosMap) => {
    let pAsc = mesObj.pantallas_ascensores || "";
    let pEsp = mesObj.pantallas_espera || "";
    let pCir = mesObj.pantallas_circulacion || "";
    let pCow = mesObj.pantallas_cowork || "";
    if (!pAsc && !pEsp && !pCir && !pCow && mesObj.edificios_ids) {
      const ids = mesObj.edificios_ids
        .split(",")
        .map((s) => Number(s.trim()))
        .filter(Boolean);
      const asc = [];
      const esp = [];
      const cir = [];
      const cow = [];
      ids.forEach((eid) => {
        const e = edificiosMap[eid];
        if (!e) return;
        if ((Number(e.pantallas) || 0) > 0) asc.push(eid);
        if ((Number(e.espera) || 0) > 0) esp.push(eid);
        if ((Number(e.totems) || 0) > 0) cir.push(eid);
        if ((Number(e.cowork) || 0) > 0) cow.push(eid);
      });
      pAsc = asc.join(",");
      pEsp = esp.join(",");
      pCir = cir.join(",");
      pCow = cow.join(",");
    }
    return { pAsc, pEsp, pCir, pCow };
  };

  // Helper: calcula comisión edificios mensual explotando JSONB meses
  const calcComisionEdificiosContratos = useCallback(
    (data, filterYear, getDolarFn, edificiosMap, clientesArr, intermediariosArr, paisCalc, snapMapArg) => {
      const valores = Array(12).fill(0);
      if (!edificiosMap || Object.keys(edificiosMap).length === 0) return valores;

      data
        .filter((c) => !c.eliminada && c.estatus === "100%")
        .forEach((c) => {
          (c.meses || []).forEach((mesObj) => {
            if (!mesObj.facturafecha) return;
            if (!mesObj.edificios_ids) return;
            const fecha = new Date(mesObj.facturafecha);
            if (isNaN(fecha.getTime())) return;
            const monthIndex = fecha.getMonth();
            const year = fecha.getFullYear();
            if (year !== filterYear || monthIndex < 0 || monthIndex >= 12) return;

            const importe = Number(mesObj.importe) || 0;
            const importeLocal = c.moneda_dolar ? importe * getDolarFn(mesObj.facturafecha) : importe;
            const { pAsc, pEsp, pCir, pCow } = resolverCamposTipo(mesObj, edificiosMap);

            const oppObj = {
              edificiosparticipantes: mesObj.edificios_ids,
              pantallastotales: mesObj.pantallas_totales || 0,
              pantallas_ascensores: pAsc,
              pantallas_espera: pEsp,
              pantallas_circulacion: pCir,
              pantallas_cowork: pCow,
              facturafecha: mesObj.facturafecha,
              importe: importeLocal,
              client_id: c.client_id,
              intermediario_override_id: c.intermediario_override_id,
              isBTL: false,
            };

            const neto = calcularImporteNetoIntermediario(oppObj, clientesArr, intermediariosArr, paisCalc);
            valores[monthIndex] += calcularComisionEdificiosTotal(oppObj, edificiosMap, neto, snapMapArg);
          });
        });
      return valores;
    },
    []
  );

  // Helper: calcula comisión agencia mensual explotando JSONB meses
  const calcComisionAgenciaContratos = useCallback(
    (data, filterYear, getDolarFn, clientesArr, intermediariosArr, paisCalc) => {
      const valores = Array(12).fill(0);
      data
        .filter((c) => !c.eliminada && c.estatus === "100%")
        .forEach((c) => {
          (c.meses || []).forEach((mesObj) => {
            if (!mesObj.facturafecha) return;
            const fecha = new Date(mesObj.facturafecha);
            if (isNaN(fecha.getTime())) return;
            const monthIndex = fecha.getMonth();
            const year = fecha.getFullYear();
            if (year !== filterYear || monthIndex < 0 || monthIndex >= 12) return;

            const importe = parseFloat(mesObj.importe) || 0;
            const importeLocal = c.moneda_dolar ? importe * getDolarFn(mesObj.facturafecha) : importe;
            const oppObj = {
              importe: importeLocal,
              client_id: c.client_id,
              intermediario_override_id: c.intermediario_override_id,
              facturafecha: mesObj.facturafecha,
              isBTL: false,
            };
            const neto = calcularImporteNetoIntermediario(oppObj, clientesArr, intermediariosArr, paisCalc);
            valores[monthIndex] += importeLocal - neto;
          });
        });
      return valores;
    },
    []
  );

  // Helper: comisión BROKER mensual de contratos (2ª capa, capa aislada/aditiva)
  // Espejo de calcComisionAgenciaContratos pero derivada de cliente.broker_id con gate
  // DOBLE (BROKER_ACTIVACION + fecha del registro) sobre mesObj.facturafecha directo.
  const calcComisionBrokerContratos = useCallback(
    (data, filterYear, getDolarFn, clientesArr, intermediariosArr, paisCalc) => {
      const valores = Array(12).fill(0);
      const activacion = new Date(BROKER_ACTIVACION);
      data
        .filter((c) => !c.eliminada && c.estatus === "100%")
        .forEach((c) => {
          (c.meses || []).forEach((mesObj) => {
            if (!mesObj.facturafecha) return;
            const fecha = new Date(mesObj.facturafecha);
            if (isNaN(fecha.getTime())) return;
            const monthIndex = fecha.getMonth();
            const year = fecha.getFullYear();
            if (year !== filterYear || monthIndex < 0 || monthIndex >= 12) return;

            const cliente = clientesArr.find((cl) => cl.id === c.client_id && cl.pais === paisCalc);
            if (!cliente?.broker_id) return;
            const broker = intermediariosArr.find(
              (i) => i.id === parseInt(cliente.broker_id, 10) && i.pais === paisCalc
            );
            if (!broker?.comision_fechainicio) return;
            // Gate doble
            if (fecha < activacion || fecha < new Date(broker.comision_fechainicio)) return;

            const importe = parseFloat(mesObj.importe) || 0;
            const importeLocal = c.moneda_dolar ? importe * getDolarFn(mesObj.facturafecha) : importe;
            valores[monthIndex] += (importeLocal * (broker.comision || 0)) / 100;
          });
        });
      return valores;
    },
    []
  );

  // Helper: DESGLOSE por factura de comisión AGENCIA de contratos (alimenta el modal).
  // Espejo EXACTO de calcComisionAgenciaContratos (misma conversión importeLocal, mismo
  // filtro, misma fórmula comisión = importeLocal − neto) pero hace push por mesObj.
  const calcDesgloseAgenciaContratos = useCallback(
    (data, filterYear, getDolarFn, clientesArr2, intermArr2, paisCalc) => {
      const comisiones = Array(12)
        .fill()
        .map(() => []);
      data
        .filter((c) => !c.eliminada && c.estatus === "100%")
        .forEach((c) => {
          (c.meses || []).forEach((mesObj) => {
            if (!mesObj.facturafecha) return;
            const fecha = new Date(mesObj.facturafecha);
            if (isNaN(fecha.getTime())) return;
            const monthIndex = fecha.getMonth();
            const year = fecha.getFullYear();
            if (year !== filterYear || monthIndex < 0 || monthIndex >= 12) return;

            const importe = parseFloat(mesObj.importe) || 0;
            const importeLocal = c.moneda_dolar ? importe * getDolarFn(mesObj.facturafecha) : importe;
            const oppObj = {
              importe: importeLocal,
              client_id: c.client_id,
              intermediario_override_id: c.intermediario_override_id,
              facturafecha: mesObj.facturafecha,
              isBTL: false,
            };
            const neto = calcularImporteNetoIntermediario(oppObj, clientesArr2, intermArr2, paisCalc);
            comisiones[monthIndex].push({
              intermediario: obtenerNombreIntermediario(oppObj, clientesArr2, intermArr2, paisCalc),
              facturanumero: mesObj.facturanumero || "—",
              comision: importeLocal - neto,
              porcentaje: calcularPorcentajeComisionIntermediario(oppObj, clientesArr2, intermArr2, paisCalc),
              importe: importeLocal,
              importeOriginalMoneda: importe,
              moneda_dolar: !!c.moneda_dolar,
              oppId: `contrato-${c.id}-${mesObj.mes ?? mesObj.facturafecha}`,
              cliente: c.cliente,
            });
          });
        });
      return comisiones;
    },
    []
  );

  // Helper: DESGLOSE por factura de comisión BROKER de contratos (alimenta el modal).
  // Espejo EXACTO de calcComisionBrokerContratos (misma conversión + gate doble) con push por mesObj.
  const calcDesgloseBrokerContratos = useCallback(
    (data, filterYear, getDolarFn, clientesArr2, intermArr2, paisCalc) => {
      const comisiones = Array(12)
        .fill()
        .map(() => []);
      const activacion = new Date(BROKER_ACTIVACION);
      data
        .filter((c) => !c.eliminada && c.estatus === "100%")
        .forEach((c) => {
          (c.meses || []).forEach((mesObj) => {
            if (!mesObj.facturafecha) return;
            const fecha = new Date(mesObj.facturafecha);
            if (isNaN(fecha.getTime())) return;
            const monthIndex = fecha.getMonth();
            const year = fecha.getFullYear();
            if (year !== filterYear || monthIndex < 0 || monthIndex >= 12) return;

            const cliente = clientesArr2.find((cl) => cl.id === c.client_id && cl.pais === paisCalc);
            if (!cliente?.broker_id) return;
            const broker = intermArr2.find((i) => i.id === parseInt(cliente.broker_id, 10) && i.pais === paisCalc);
            if (!broker?.comision_fechainicio) return;
            if (fecha < activacion || fecha < new Date(broker.comision_fechainicio)) return;

            const importe = parseFloat(mesObj.importe) || 0;
            const importeLocal = c.moneda_dolar ? importe * getDolarFn(mesObj.facturafecha) : importe;
            const porcentajeBroker = broker.comision || 0;
            comisiones[monthIndex].push({
              brokerNombre: broker.nombre,
              facturanumero: mesObj.facturanumero || "—",
              comisionBroker: (importeLocal * porcentajeBroker) / 100,
              porcentajeBroker,
              importe: importeLocal,
              importeOriginalMoneda: importe,
              moneda_dolar: !!c.moneda_dolar,
              oppId: `contrato-${c.id}-${mesObj.mes ?? mesObj.facturafecha}`,
              cliente: c.cliente,
            });
          });
        });
      return comisiones;
    },
    []
  );

  // Helper: calcula comisión comercial mensual explotando JSONB meses
  const calcComisionComercialContratos = useCallback(
    (data, filterYear, getDolarFn, clientesMap, intermediariosMap, edificiosMap, paisCalc, snapMapArg) => {
      const valores = Array(12).fill(0);
      data
        .filter((c) => !c.eliminada && c.estatus === "100%")
        .forEach((c) => {
          (c.meses || []).forEach((mesObj) => {
            if (!mesObj.facturafecha) return;
            const fecha = new Date(mesObj.facturafecha);
            if (isNaN(fecha.getTime())) return;
            const monthIndex = fecha.getMonth();
            const year = fecha.getFullYear();
            if (year !== filterYear || monthIndex < 0 || monthIndex >= 12) return;

            const importe = Number(mesObj.importe) || 0;
            const importeLocal = c.moneda_dolar ? importe * getDolarFn(mesObj.facturafecha) : importe;
            const { pAsc, pEsp, pCir, pCow } = resolverCamposTipo(mesObj, edificiosMap || {});
            const oppObj = {
              importe: importeLocal,
              client_id: c.client_id,
              intermediario_override_id: c.intermediario_override_id,
              pais: paisCalc,
              facturafecha: mesObj.facturafecha,
              ejecutivo: c.ejecutivo,
              mes_cierre: c.mes_cierre,
              moneda_dolar: false,
              isBTL: false,
              edificiosparticipantes: mesObj.edificios_ids || "",
              pantallastotales: mesObj.pantallas_totales || 0,
              pantallas_ascensores: pAsc,
              pantallas_espera: pEsp,
              pantallas_circulacion: pCir,
              pantallas_cowork: pCow,
            };
            valores[monthIndex] += calculateComission.calculateSalesCommission(
              oppObj,
              clientesMap,
              intermediariosMap,
              edificiosMap,
              undefined,
              snapMapArg
            );
          });
        });
      return valores;
    },
    []
  );

  // País actual
  const contratosData = pais === "Chile" ? contratosChile : contratosPeru;

  // Memoize arrays para evitar re-renders en dependencias de useMemo
  const clientesArr = useMemo(() => (clientes ? Object.values(clientes) : []), [clientes]);
  const intermediariosArr = useMemo(() => (intermediarios ? Object.values(intermediarios) : []), [intermediarios]);
  const clientesChileArr = useMemo(() => (clientesChile ? Object.values(clientesChile) : []), [clientesChile]);
  const intermediariosChileArr = useMemo(
    () => (intermediariosChile ? Object.values(intermediariosChile) : []),
    [intermediariosChile]
  );
  const clientesPeruArr = useMemo(() => (clientesPeru ? Object.values(clientesPeru) : []), [clientesPeru]);
  const intermediariosPeruArr = useMemo(
    () => (intermediariosPeru ? Object.values(intermediariosPeru) : []),
    [intermediariosPeru]
  );

  // ─── FACTURAS POR MES (para InvoiceModal) ───

  const facturasPorMesContratos = useMemo(() => {
    const facturas = Array(12)
      .fill()
      .map(() => []);
    contratosData
      .filter((c) => !c.eliminada && c.estatus === "100%")
      .forEach((c) => {
        (c.meses || []).forEach((mesObj) => {
          if (!mesObj.facturafecha || !mesObj.facturanumero) return;
          const fecha = new Date(mesObj.facturafecha);
          if (isNaN(fecha.getTime())) return;
          const monthIndex = fecha.getMonth();
          const year = fecha.getFullYear();
          if (year !== selectedYear || monthIndex < 0 || monthIndex >= 12) return;
          const importe = Number(mesObj.importe) || 0;
          const importeLocal = c.moneda_dolar ? importe * getDolarValue(mesObj.facturafecha) : importe;
          facturas[monthIndex].push({
            facturanumero: mesObj.facturanumero,
            cliente: c.cliente || "Sin cliente",
            importe: importeLocal,
            importeOriginalMoneda: importe,
            moneda_dolar: !!c.moneda_dolar,
          });
        });
      });
    return facturas;
  }, [contratosData, selectedYear, getDolarValue]);

  // ─── COMISIONES EDIFICIOS POR MES (para BuildingCommissionModal) ───

  const comisionesEdificiosPorMesContratos = useMemo(() => {
    const comisiones = Array(12)
      .fill()
      .map(() => []);
    if (!edificios || Object.keys(edificios).length === 0) return comisiones;
    contratosData
      .filter((c) => !c.eliminada && c.estatus === "100%")
      .forEach((c) => {
        (c.meses || []).forEach((mesObj) => {
          if (!mesObj.facturafecha || !mesObj.edificios_ids) return;
          const fecha = new Date(mesObj.facturafecha);
          if (isNaN(fecha.getTime())) return;
          const monthIndex = fecha.getMonth();
          const year = fecha.getFullYear();
          if (year !== selectedYear || monthIndex < 0 || monthIndex >= 12) return;

          const importe = Number(mesObj.importe) || 0;
          const importeLocal = c.moneda_dolar ? importe * getDolarValue(mesObj.facturafecha) : importe;
          const { pAsc, pEsp, pCir, pCow } = resolverCamposTipo(mesObj, edificios);
          const oppObj = {
            edificiosparticipantes: mesObj.edificios_ids,
            pantallastotales: mesObj.pantallas_totales || 0,
            pantallas_ascensores: pAsc,
            pantallas_espera: pEsp,
            pantallas_circulacion: pCir,
            pantallas_cowork: pCow,
            facturafecha: mesObj.facturafecha,
            importe: importeLocal,
            client_id: c.client_id,
            intermediario_override_id: c.intermediario_override_id,
            isBTL: false,
          };
          const neto = calcularImporteNetoIntermediario(oppObj, clientesArr, intermediariosArr, pais);
          const totalComision = calcularComisionEdificiosTotal(oppObj, edificios, neto, snapMap);
          const edIds = mesObj.edificios_ids ? [...new Set(mesObj.edificios_ids.split(",").map(Number))] : [];
          comisiones[monthIndex].push({
            facturanumero: mesObj.facturanumero || "—",
            cliente: c.cliente || "Sin cliente",
            totalComision,
            soloFijos: false,
            sinEdificios: edIds.length === 0,
          });
        });
      });
    return comisiones;
  }, [contratosData, selectedYear, getDolarValue, edificios, snapMap, clientesArr, intermediariosArr, pais]);

  // ─── INGRESOS ───

  const ingresosContratosMensual = useMemo(
    () => calcIngresosContratos(contratosData, selectedYear, getDolarValue),
    [calcIngresosContratos, contratosData, selectedYear, getDolarValue]
  );

  const ingresosContratosMensualAnoAnterior = useMemo(
    () => calcIngresosContratos(contratosData, selectedYear - 1, getDolarValue),
    [calcIngresosContratos, contratosData, selectedYear, getDolarValue]
  );

  const ingresosContratosMensualChile = useMemo(
    () => calcIngresosContratos(contratosChile, selectedYear, getDolarValueChile),
    [calcIngresosContratos, contratosChile, selectedYear, getDolarValueChile]
  );

  const ingresosContratosMensualPeru = useMemo(
    () => calcIngresosContratos(contratosPeru, selectedYear, getDolarValuePeru),
    [calcIngresosContratos, contratosPeru, selectedYear, getDolarValuePeru]
  );

  // Combinado en USD (para modo región)
  const contratosMensualesCombinados = useMemo(() => {
    if (!modoCombinado) return null;
    const resultado = Array(12).fill(0);
    for (let i = 0; i < 12; i++) {
      const valorChile = ingresosContratosMensualChile[i] || 0;
      const dolarChile = dolarMensualChile[i] || dolarFallback.Chile;
      const chileUSD = valorChile / dolarChile;

      const valorPeru = ingresosContratosMensualPeru[i] || 0;
      const dolarPeru = dolarMensualPeru[i] || dolarFallback["Perú"];
      const peruUSD = valorPeru / dolarPeru;

      resultado[i] = chileUSD + peruUSD;
    }
    return resultado;
  }, [
    modoCombinado,
    ingresosContratosMensualChile,
    ingresosContratosMensualPeru,
    dolarMensualChile,
    dolarMensualPeru,
    dolarFallback,
  ]);

  // ─── COMISIÓN EDIFICIOS de contratos ───

  const comisionEdificiosContratosMensual = useMemo(
    () =>
      calcComisionEdificiosContratos(
        contratosData,
        selectedYear,
        getDolarValue,
        edificios,
        clientesArr,
        intermediariosArr,
        pais,
        snapMap
      ),
    [
      calcComisionEdificiosContratos,
      contratosData,
      selectedYear,
      getDolarValue,
      edificios,
      snapMap,
      clientesArr,
      intermediariosArr,
      pais,
    ]
  );

  const comisionEdificiosContratosMensualAnoAnterior = useMemo(
    () =>
      calcComisionEdificiosContratos(
        contratosData,
        selectedYear - 1,
        getDolarValue,
        edificios,
        clientesArr,
        intermediariosArr,
        pais,
        snapMap
      ),
    [
      calcComisionEdificiosContratos,
      contratosData,
      selectedYear,
      getDolarValue,
      edificios,
      snapMap,
      clientesArr,
      intermediariosArr,
      pais,
    ]
  );

  const comisionEdificiosContratosMensualChile = useMemo(
    () =>
      calcComisionEdificiosContratos(
        contratosChile,
        selectedYear,
        getDolarValueChile,
        edificiosChile,
        clientesChileArr,
        intermediariosChileArr,
        "Chile",
        snapMapChile
      ),
    [
      calcComisionEdificiosContratos,
      contratosChile,
      selectedYear,
      getDolarValueChile,
      edificiosChile,
      snapMapChile,
      clientesChileArr,
      intermediariosChileArr,
    ]
  );

  const comisionEdificiosContratosMensualPeru = useMemo(
    () =>
      calcComisionEdificiosContratos(
        contratosPeru,
        selectedYear,
        getDolarValuePeru,
        edificiosPeru,
        clientesPeruArr,
        intermediariosPeruArr,
        "Perú",
        snapMapPeru
      ),
    [
      calcComisionEdificiosContratos,
      contratosPeru,
      selectedYear,
      getDolarValuePeru,
      edificiosPeru,
      snapMapPeru,
      clientesPeruArr,
      intermediariosPeruArr,
    ]
  );

  // ─── COMISIÓN AGENCIA de contratos ───

  const comisionAgenciaContratosMensual = useMemo(
    () =>
      calcComisionAgenciaContratos(contratosData, selectedYear, getDolarValue, clientesArr, intermediariosArr, pais),
    [calcComisionAgenciaContratos, contratosData, selectedYear, getDolarValue, clientesArr, intermediariosArr, pais]
  );

  const comisionAgenciaContratosMensualAnoAnterior = useMemo(
    () =>
      calcComisionAgenciaContratos(
        contratosData,
        selectedYear - 1,
        getDolarValue,
        clientesArr,
        intermediariosArr,
        pais
      ),
    [calcComisionAgenciaContratos, contratosData, selectedYear, getDolarValue, clientesArr, intermediariosArr, pais]
  );

  const comisionAgenciaContratosMensualChile = useMemo(
    () =>
      calcComisionAgenciaContratos(
        contratosChile,
        selectedYear,
        getDolarValueChile,
        clientesChileArr,
        intermediariosChileArr,
        "Chile"
      ),
    [
      calcComisionAgenciaContratos,
      contratosChile,
      selectedYear,
      getDolarValueChile,
      clientesChileArr,
      intermediariosChileArr,
    ]
  );

  const comisionAgenciaContratosMensualPeru = useMemo(
    () =>
      calcComisionAgenciaContratos(
        contratosPeru,
        selectedYear,
        getDolarValuePeru,
        clientesPeruArr,
        intermediariosPeruArr,
        "Perú"
      ),
    [
      calcComisionAgenciaContratos,
      contratosPeru,
      selectedYear,
      getDolarValuePeru,
      clientesPeruArr,
      intermediariosPeruArr,
    ]
  );

  // ─── COMISIÓN BROKER de contratos (capa aislada/aditiva) ───
  const comisionBrokerContratosMensual = useMemo(
    () => calcComisionBrokerContratos(contratosData, selectedYear, getDolarValue, clientesArr, intermediariosArr, pais),
    [calcComisionBrokerContratos, contratosData, selectedYear, getDolarValue, clientesArr, intermediariosArr, pais]
  );

  const comisionBrokerContratosMensualAnoAnterior = useMemo(
    () =>
      calcComisionBrokerContratos(contratosData, selectedYear - 1, getDolarValue, clientesArr, intermediariosArr, pais),
    [calcComisionBrokerContratos, contratosData, selectedYear, getDolarValue, clientesArr, intermediariosArr, pais]
  );

  const comisionBrokerContratosMensualChile = useMemo(
    () =>
      calcComisionBrokerContratos(
        contratosChile,
        selectedYear,
        getDolarValueChile,
        clientesChileArr,
        intermediariosChileArr,
        "Chile"
      ),
    [
      calcComisionBrokerContratos,
      contratosChile,
      selectedYear,
      getDolarValueChile,
      clientesChileArr,
      intermediariosChileArr,
    ]
  );

  const comisionBrokerContratosMensualPeru = useMemo(
    () =>
      calcComisionBrokerContratos(
        contratosPeru,
        selectedYear,
        getDolarValuePeru,
        clientesPeruArr,
        intermediariosPeruArr,
        "Perú"
      ),
    [
      calcComisionBrokerContratos,
      contratosPeru,
      selectedYear,
      getDolarValuePeru,
      clientesPeruArr,
      intermediariosPeruArr,
    ]
  );

  // ─── DESGLOSE por factura de contratos (solo para el modal "Costo intermediación") ───
  const comisionesAgenciaContratosPorMes = useMemo(
    () =>
      calcDesgloseAgenciaContratos(contratosData, selectedYear, getDolarValue, clientesArr, intermediariosArr, pais),
    [calcDesgloseAgenciaContratos, contratosData, selectedYear, getDolarValue, clientesArr, intermediariosArr, pais]
  );
  const comisionesBrokerContratosPorMes = useMemo(
    () => calcDesgloseBrokerContratos(contratosData, selectedYear, getDolarValue, clientesArr, intermediariosArr, pais),
    [calcDesgloseBrokerContratos, contratosData, selectedYear, getDolarValue, clientesArr, intermediariosArr, pais]
  );

  // ─── COMISIÓN COMERCIAL de contratos ───

  const comisionComercialContratosMensual = useMemo(
    () =>
      calcComisionComercialContratos(
        contratosData,
        selectedYear,
        getDolarValue,
        clientes,
        intermediarios,
        edificios,
        pais,
        snapMap
      ),
    [
      calcComisionComercialContratos,
      contratosData,
      selectedYear,
      getDolarValue,
      clientes,
      intermediarios,
      edificios,
      snapMap,
      pais,
    ]
  );

  const comisionComercialContratosMensualAnoAnterior = useMemo(
    () =>
      calcComisionComercialContratos(
        contratosData,
        selectedYear - 1,
        getDolarValue,
        clientes,
        intermediarios,
        edificios,
        pais,
        snapMap
      ),
    [
      calcComisionComercialContratos,
      contratosData,
      selectedYear,
      getDolarValue,
      clientes,
      intermediarios,
      edificios,
      snapMap,
      pais,
    ]
  );

  const comisionComercialContratosMensualChile = useMemo(
    () =>
      calcComisionComercialContratos(
        contratosChile,
        selectedYear,
        getDolarValueChile,
        clientesChile,
        intermediariosChile,
        edificiosChile,
        "Chile",
        snapMapChile
      ),
    [
      calcComisionComercialContratos,
      contratosChile,
      selectedYear,
      getDolarValueChile,
      clientesChile,
      intermediariosChile,
      edificiosChile,
      snapMapChile,
    ]
  );

  const comisionComercialContratosMensualPeru = useMemo(
    () =>
      calcComisionComercialContratos(
        contratosPeru,
        selectedYear,
        getDolarValuePeru,
        clientesPeru,
        intermediariosPeru,
        edificiosPeru,
        "Perú",
        snapMapPeru
      ),
    [
      calcComisionComercialContratos,
      contratosPeru,
      selectedYear,
      getDolarValuePeru,
      clientesPeru,
      intermediariosPeru,
      edificiosPeru,
      snapMapPeru,
    ]
  );

  return {
    facturasPorMesContratos,
    comisionesEdificiosPorMesContratos,
    ingresosContratosMensual,
    ingresosContratosMensualAnoAnterior,
    ingresosContratosMensualChile,
    ingresosContratosMensualPeru,
    contratosMensualesCombinados,
    comisionEdificiosContratosMensual,
    comisionEdificiosContratosMensualAnoAnterior,
    comisionEdificiosContratosMensualChile,
    comisionEdificiosContratosMensualPeru,
    comisionAgenciaContratosMensual,
    comisionAgenciaContratosMensualAnoAnterior,
    comisionAgenciaContratosMensualChile,
    comisionAgenciaContratosMensualPeru,
    comisionBrokerContratosMensual,
    comisionBrokerContratosMensualAnoAnterior,
    comisionBrokerContratosMensualChile,
    comisionBrokerContratosMensualPeru,
    comisionesAgenciaContratosPorMes,
    comisionesBrokerContratosPorMes,
    comisionComercialContratosMensual,
    comisionComercialContratosMensualAnoAnterior,
    comisionComercialContratosMensualChile,
    comisionComercialContratosMensualPeru,
  };
}
